import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ReactLoading from "react-loading";
import Footer from "../../../components/footer";
import PageBackgroundPattern from "../../../components/PageBackgroundPattern";
import BuscarPartidoHeader from "../components/BuscarPartidoHeader";
import BuscarPartidoFilters from "../components/BuscarPartidoFilters";
import BuscarPartidoResults from "../components/BuscarPartidoResults";
import useBuscarPartidoFilters from "../hooks/useBuscarPartidoFilters";
import buildBuscarPartidoUrl from "../hooks/buildBuscarPartidoUrl";
import { fetchBuscarPartidoFilters, fetchBuscarPartidoResults } from "../hooks/useBuscarPartidoApi";

const BUSCAR_PARTIDO_STATE_KEY = 'buscarPartidoLastSearch';

const extractSeasonStartYear = (seasonLabel = '') => {
    const match = String(seasonLabel).match(/(\d{4})(?:-(\d{2,4}))?/);
    if (!match) return Number.NEGATIVE_INFINITY;
    return parseInt(match[1], 10);
};

const isSplitSeasonLabel = (seasonLabel = '') => {
    return /(\d{4})-(\d{2,4})/.test(String(seasonLabel));
};

const sortTemporadasLikePlayoffs = (temporadas = []) => {
    return [...temporadas].sort((left, right) => {
        const yearDiff = extractSeasonStartYear(right) - extractSeasonStartYear(left);
        if (yearDiff !== 0) return yearDiff;

        const leftIsSplitSeason = isSplitSeasonLabel(left);
        const rightIsSplitSeason = isSplitSeasonLabel(right);

        if (leftIsSplitSeason !== rightIsSplitSeason) {
            return leftIsSplitSeason ? -1 : 1;
        }

        return String(left).localeCompare(String(right), 'es');
    });
};

const readStoredBuscarPartidoState = () => {
    try {
        const rawState = sessionStorage.getItem(BUSCAR_PARTIDO_STATE_KEY);
        if (!rawState) return null;

        const parsedState = JSON.parse(rawState);
        const hasFilters = parsedState && typeof parsedState === 'object' && parsedState.filters;
        const hasDataArray = Array.isArray(parsedState?.data);

        if (!hasFilters || !hasDataArray) {
            return null;
        }

        return {
            filters: parsedState.filters,
            data: parsedState.data,
        };
    } catch (error) {
        return null;
    }
};

const storeBuscarPartidoState = ({ filters, data }) => {
    try {
        sessionStorage.setItem(
            BUSCAR_PARTIDO_STATE_KEY,
            JSON.stringify({
                filters,
                data,
            }),
        );
    } catch (error) {
        // Ignore sessionStorage errors to avoid blocking UI updates.
    }
};

const clearStoredBuscarPartidoState = () => {
    try {
        sessionStorage.removeItem(BUSCAR_PARTIDO_STATE_KEY);
    } catch (error) {
        // Ignore sessionStorage errors.
    }
};

function BuscarPartido() {

    const [searchParams] = useSearchParams();
    const [data, setData] = useState([]);
    const [ligas, setLigas] = useState([]);
    const [canchas, setCanchas] = useState([]);
    const [adversarios, setAdversarios] = useState([]);
    const [isFiltersLoading, setIsFiltersLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [jugados, setJugados] = useState(0);
    const [ganados, setGanados] = useState(0);
    const [perdidos, setPerdidos] = useState(0);
    const {
        filters,
        setFilter,
        setAllFilters,
        resetFilters,
        options: {
            etapas,
            localias,
            resultados,
            alargues,
        },
    } = useBuscarPartidoFilters();

    useEffect(() => {
        const ligaParam = searchParams.get('liga');
        const adversarioParam = searchParams.get('adversario');

        setIsFiltersLoading(true);
        fetchBuscarPartidoFilters()
            .then(([dataLigas, dataCanchas, dataAdversarios]) => {
                setLigas(sortTemporadasLikePlayoffs(dataLigas));
                setCanchas(dataCanchas);
                setAdversarios(dataAdversarios);
            })
            .catch(() => {
                setLigas([]);
                setCanchas([]);
                setAdversarios([]);
            })
            .finally(() => {
                setIsFiltersLoading(false);
            });

        document.title = 'Buscar partido';

        if (ligaParam || adversarioParam) {
            const filtersFromParams = {
                torneo: ligaParam ?? '',
                etapa: '',
                cancha: '',
                adversario: adversarioParam ?? '',
                localia: '',
                resultado: '',
                alargue: '',
            };

            setAllFilters(filtersFromParams);
            setIsLoading(true);
            const url = buildBuscarPartidoUrl(filtersFromParams);
            fetchBuscarPartidoResults(url)
                .then((result) => {
                    setData(result);
                    setDataPartidos(result);
                    storeBuscarPartidoState({
                        filters: filtersFromParams,
                        data: result,
                    });
                })
                .catch(() => { setData([]); setDataPartidos([]); })
                .finally(() => setIsLoading(false));

            return;
        }

        const storedSearchState = readStoredBuscarPartidoState();

        if (storedSearchState) {
            setAllFilters(storedSearchState.filters);
            setData(storedSearchState.data);
            setDataPartidos(storedSearchState.data);
        }
    }, [searchParams, setAllFilters]);

    const setDataPartidos = (partidos) => {
        let partidosGanados = 0;
        let partidosPerdidos = 0;

        for (let i = 0; i < partidos.length; i++) {
            if (partidos[i].ganado) partidosGanados++;
            if (!partidos[i].ganado) partidosPerdidos++;
        }

        setJugados(partidos.length);
        setGanados(partidosGanados);
        setPerdidos(partidosPerdidos);
    };

    const getEtapaFromPartido = (partido) => {
        if (partido.playoffsEtapa) return partido.playoffsEtapa;
        if (partido.temporadaRegular) return `Temp. Reg. Fecha ${partido.fechaLiga}`;
        if (partido.superLiga) return `SuperLiga Fecha ${partido.fechaLiga}`;
        if (partido.liguilla) return `Liguilla Fecha ${partido.fechaLiga}`;
        if (partido.reclasificatorio) return `Reclasificatorio Fecha ${partido.fechaLiga}`;
        if (partido.fase) return `${partido.fase} Partido ${partido.fechaLiga}`;
        return `Partido ${partido.fechaLiga}`;
    };

    const getResultadoFromPartido = (partido) => {
        let scoreAguada = partido?.totalAguadaStats?.totalPuntos ?? null;
        let scoreRival = partido?.totalAdversarioStats?.totalPuntos ?? null;

        if (scoreAguada === null || scoreRival === null) {
            const resultado = partido?.resultado ?? '';
            const scores = resultado.match(/\d+/g) || ['0', '0'];
            const score1 = parseInt(scores[0]) || 0;
            const score2 = parseInt(scores[1]) || 0;
            const aguadaIsFirst = resultado.startsWith('Aguada');
            scoreAguada = aguadaIsFirst ? score1 : score2;
            scoreRival = aguadaIsFirst ? score2 : score1;
        }

        return {
            label: partido.ganado ? 'Victoria' : 'Derrota',
            scoreAguada,
            scoreRival,
        };
    };

    const limpiarFiltros = () => {
        resetFilters();

        setData([]);
        setDataPartidos([]);
        clearStoredBuscarPartidoState();
    };

    const scrollToTop = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const buscarPartido = async () => {
        setIsLoading(true);
        const url = buildBuscarPartidoUrl(filters);

        try {
            const result = await fetchBuscarPartidoResults(url);
            setData(result);
            setDataPartidos(result);
            storeBuscarPartidoState({
                filters,
                data: result,
            });
            scrollToTop();
        } catch (error) {
            setData([]);
            setDataPartidos([]);
            storeBuscarPartidoState({
                filters,
                data: [],
            });
            scrollToTop();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#062a1a] pt-16">
            <PageBackgroundPattern />
            <div className="relative z-10">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-10 pt-2 pb-6 sm:pb-8">
                <BuscarPartidoHeader />

                {isFiltersLoading ? (
                    <div className="rounded-xl border border-white/10 bg-[#102216] p-10">
                        <ReactLoading type="bars" color="#22c55e" width={80} className="text-center m-auto mb-10" />
                        <p className="text-center text-slate-200 text-lg font-semibold">Cargando filtros de búsqueda...</p>
                    </div>
                ) : (
                    <main className="flex-1 flex flex-col md:flex-row w-full gap-4 sm:gap-6 md:gap-8">
                        <aside className="w-full md:w-72 flex flex-col gap-4 sm:gap-6 shrink-0">
                            <BuscarPartidoFilters
                                ligas={ligas}
                                etapas={etapas}
                                canchas={canchas}
                                adversarios={adversarios}
                                localias={localias}
                                resultados={resultados}
                                alargues={alargues}
                                torneo={filters.torneo}
                                etapa={filters.etapa}
                                cancha={filters.cancha}
                                adversario={filters.adversario}
                                localia={filters.localia}
                                resultado={filters.resultado}
                                alargue={filters.alargue}
                                onTorneoChange={(value) => setFilter('torneo', value)}
                                onEtapaChange={(value) => setFilter('etapa', value)}
                                onCanchaChange={(value) => setFilter('cancha', value)}
                                onAdversarioChange={(value) => setFilter('adversario', value)}
                                onLocaliaChange={(value) => setFilter('localia', value)}
                                onResultadoChange={(value) => setFilter('resultado', value)}
                                onAlargueChange={(value) => setFilter('alargue', value)}
                                onBuscar={buscarPartido}
                                onLimpiar={limpiarFiltros}
                            />
                        </aside>

                        <BuscarPartidoResults
                            jugados={jugados}
                            ganados={ganados}
                            perdidos={perdidos}
                            isLoading={isLoading}
                            data={data}
                            getResultadoFromPartido={getResultadoFromPartido}
                            getEtapaFromPartido={getEtapaFromPartido}
                        />
                    </main>
                )}
                </div>
                <Footer></Footer>
            </div>
        </div>
    );
}

export default BuscarPartido;
